import { useEffect, useMemo, useState } from 'react'
import { api } from '../lib/api.js'
import { Chip, Empty, ErrorNote } from '../components/ui.jsx'

// Outreach segments.
//
// The fit models say whether a company structurally CARRIES the problem. They
// do not say what we can honestly open an email with, and that is a different
// question -- it depends on what management has already disclosed. A company
// that publishes a working-capital ratio and is missing it needs a completely
// different first line from one that has never mentioned the subject, even
// though both can score Strong.
//
// So the segments come from the disclosure and the evidence inside the email
// comes from the fit criteria. The test for a segment is that ONE email works
// for every account in it; anything finer belongs on the accounts list as a
// filter.

const FIT_KIND = {
  Strong: 'named',
  Possible: 'mentioned',
  Weak: 'not_now',
  'Below gate': 'absent',
}

const money = (m) => (m ? Math.round(m).toLocaleString() : '—')

function FitCell({ band }) {
  if (!band) return <span style={{ color: 'var(--tertiary)' }}>—</span>
  return <Chip kind={FIT_KIND[band] || 'unresearched'}>{band}</Chip>
}

function Tile({ seg, on, onClick, muted }) {
  return (
    <button className={`filter-tile ${on ? 'on' : ''} ${muted ? 'nd' : ''}`}
      style={{ minWidth: 168, maxWidth: 214 }} onClick={onClick} title={seg.observation || seg.reason}>
      <span className="ft-name" style={{ lineHeight: 1.25 }}>{seg.label}</span>
      <span className="ft-meta">
        {seg.count} · {seg.contacts} contacts{seg.excess_usd ? ` · ${money(seg.excess_usd)}m` : ''}
      </span>
    </button>
  )
}

// The example is there to be lifted, so it has to leave the app as text rather
// than as something you reselect by hand out of a scrolling panel.
function CopyButton({ text }) {
  const [done, setDone] = useState(false)
  return (
    <button className="btn" onClick={async () => {
      try {
        await navigator.clipboard.writeText(text)
        setDone(true)
        setTimeout(() => setDone(false), 1400)
      } catch { /* clipboard blocked: the text is on screen and selectable anyway */ }
    }}>
      {done ? 'Copied' : 'Copy'}
    </button>
  )
}

function Brief({ seg }) {
  const full = `Subject: ${seg.example.subject}\n\n${seg.example.body}`
  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div className="card-header" style={{ flexWrap: 'wrap', gap: 6 }}>
        <span className="card-title">{seg.label}</span>
        <Chip kind="plain">{seg.band}</Chip>
        <span className="card-sub" style={{ marginLeft: 'auto' }}>{seg.audience}</span>
      </div>

      <div style={{ padding: '9px 12px', overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 11 }}>
        <div>
          <div style={{ marginBottom: 4 }}>{seg.observation}</div>
          <div className="card-sub">{seg.evidence}</div>
        </div>

        <div>
          <div className="lbl" style={{ marginBottom: 5 }}>Points to address</div>
          <ol style={{ margin: 0, paddingLeft: 17, display: 'flex', flexDirection: 'column', gap: 5 }}>
            {seg.bullets.map((b, i) => (
              <li key={i} style={{ fontSize: 'var(--fs-pill)', lineHeight: 1.45 }}>{b}</li>
            ))}
          </ol>
        </div>

        {seg.caution ? (
          <div className="notice" style={{ fontSize: 'var(--fs-pill)', lineHeight: 1.45 }}>{seg.caution}</div>
        ) : null}

        <div>
          <div className="row" style={{ marginBottom: 5, alignItems: 'baseline' }}>
            <span className="lbl">Example</span>
            <span className="card-sub" style={{ marginLeft: 6 }}>{seg.example.to}</span>
            <span style={{ marginLeft: 'auto' }}><CopyButton text={full} /></span>
          </div>
          <div style={{ border: '0.5px solid var(--line)', background: 'var(--bg)', padding: '8px 10px' }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>{seg.example.subject}</div>
            <div style={{ fontSize: 'var(--fs-pill)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
              {seg.example.body}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function Summary({ onOpen }) {
  const [data, setData] = useState(null)
  const [err, setErr] = useState(null)
  const [key, setKey] = useState('missing-target')

  useEffect(() => { api.segments().then(setData).catch(setErr) }, [])

  const all = useMemo(
    () => (data ? [...data.segments, ...data.overlays, data.suppress] : []),
    [data],
  )

  if (err) return <div className="body"><ErrorNote error={err} /></div>
  if (!data) return <div className="body"><div className="empty">Loading segments…</div></div>

  const seg = all.find((s) => s.key === key) || all[0]
  const t = data.totals
  const wc = data.segments.filter((s) => s.discipline === 'Working capital')
  const proc = data.segments.filter((s) => s.discipline === 'Procurement')

  return (
    <>
      <div className="topbar">
        <h1>Summary</h1>
        <span className="topbar-note">
          {t.reached} of {t.accounts} accounts reachable · {t.reached_contacts} contacts ·
          {' '}{money(t.excess_usd)}m of excess inventory quantified
        </span>
      </div>

      <div className="body" style={{ display: 'flex', flexDirection: 'column', gap: 10, minHeight: 0 }}>
        <div className="card">
          <div className="card-header">
            <span className="card-title">Outreach segments</span>
            <span className="card-sub" style={{ marginLeft: 8 }}>
              Grouped by what the company has disclosed, because that decides the opening line.
              One email has to work for every account in a segment.
            </span>
          </div>

          <div style={{ padding: '9px 12px', display: 'flex', flexDirection: 'column', gap: 7 }}>
            <div className="row" style={{ gap: 7, flexWrap: 'wrap', alignItems: 'stretch' }}>
              <span className="lbl" style={{ alignSelf: 'center', width: 76 }}>Working capital</span>
              {wc.map((s) => <Tile key={s.key} seg={s} on={s.key === seg.key} onClick={() => setKey(s.key)} />)}
            </div>
            <div className="row" style={{ gap: 7, flexWrap: 'wrap', alignItems: 'stretch' }}>
              <span className="lbl" style={{ alignSelf: 'center', width: 76 }}>Procurement</span>
              {proc.map((s) => <Tile key={s.key} seg={s} on={s.key === seg.key} onClick={() => setKey(s.key)} />)}
            </div>
            <div className="row" style={{ gap: 7, flexWrap: 'wrap', alignItems: 'stretch' }}>
              <span className="lbl" style={{ alignSelf: 'center', width: 76 }}>Overlays</span>
              {data.overlays.map((s) => (
                <Tile key={s.key} seg={s} on={s.key === seg.key} onClick={() => setKey(s.key)} />
              ))}
              {/* Not a segment -- a do-not-send list, so it reads as muted. */}
              <Tile seg={data.suppress} muted on={seg.key === 'suppress'} onClick={() => setKey('suppress')} />
              <span className="card-sub" style={{ alignSelf: 'center' }}>
                {t.unassigned} not yet researched
              </span>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, flex: 1, minHeight: 0, alignItems: 'stretch' }}>
          <div style={{ width: 440, minWidth: 380, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {seg.example ? (
              <Brief seg={seg} />
            ) : (
              <div className="card">
                <div className="card-header"><span className="card-title">{seg.label}</span></div>
                <div style={{ padding: '9px 12px', fontSize: 'var(--fs-pill)', lineHeight: 1.5 }}>
                  {seg.reason}
                </div>
              </div>
            )}
          </div>

          <div className="card" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div className="card-header">
              <span className="card-title">{seg.count} accounts</span>
              <span className="card-sub" style={{ marginLeft: 'auto' }}>
                {seg.contacts} contacts{seg.excess_usd ? ` · ${money(seg.excess_usd)}m excess inventory` : ''}
              </span>
            </div>
            <div className="tbl-wrap">
              {seg.accounts.length === 0 ? (
                <Empty>Nothing in this segment.</Empty>
              ) : (
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Company</th>
                      <th style={{ width: 92 }}>Ownership</th>
                      <th className="mid" style={{ width: 92 }}>WC fit</th>
                      <th className="mid" style={{ width: 92 }}>Proc fit</th>
                      <th className="num" style={{ width: 66 }}>Inv days</th>
                      <th className="num" style={{ width: 94 }}>Excess<span className="th-unit">USD m</span></th>
                      <th className="num" style={{ width: 62 }}>Contacts</th>
                    </tr>
                  </thead>
                  <tbody>
                    {seg.accounts.map((a) => (
                      <tr key={a.slug} onClick={() => onOpen(a.slug)} style={{ cursor: 'pointer' }}>
                        <td style={{ fontWeight: 500 }}>{a.name}</td>
                        <td style={{ color: 'var(--muted)' }}>{a.ownership || '—'}</td>
                        <td className="mid"><FitCell band={a.wc_fit} /></td>
                        <td className="mid"><FitCell band={a.proc_fit} /></td>
                        <td className="num mono">{a.inv_days == null ? '—' : Math.round(a.inv_days)}</td>
                        <td className="num mono"
                          title={a.excess_days ? `${a.excess_days} days over the threshold` : undefined}>
                          {money(a.excess_usd)}
                        </td>
                        <td className="num mono">{a.contacts || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            {seg.unresolved?.length ? (
              <div className="row" style={{ padding: '7px 12px', borderTop: '0.5px solid var(--line)' }}>
                <span className="card-sub">Not matched to an account: {seg.unresolved.join(', ')}</span>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </>
  )
}
